import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { STATION, pick } from "@/demo/data";

function Stat({ label, value, unit, accent = false, testId }) {
  return (
    <div className="surface-2 p-4" data-testid={testId}>
      <span className="label">{label}</span>
      <p className={`mono mt-2 text-2xl font-semibold leading-none md:text-3xl ${accent ? "text-[var(--mint)]" : "text-[var(--warm)]"}`}>
        {value}
        {unit && <span className="ml-1 text-sm font-medium text-[var(--muted-2)]">{unit}</span>}
      </p>
    </div>
  );
}

export function OperatorView() {
  const { t, lang } = useLang();
  const [completed, setCompleted] = useState(STATION.completedToday);
  const [bundleIdx, setBundleIdx] = useState(0);
  const [toast, setToast] = useState(null);
  const [defects, setDefects] = useState([]);

  const bundles = [STATION.bundle.id, ...STATION.nextBundles];
  const bundle = bundles[bundleIdx % bundles.length];
  const hoursElapsed = 7;
  const efficiency = Math.round((completed / (STATION.targetHr * hoursElapsed)) * 100);

  const timer = useRef(null);
  const flash = (msg) => {
    setToast(msg);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 2200);
  };

  const completeBundle = () => {
    setCompleted((c) => c + STATION.bundle.qty);
    setBundleIdx((i) => i + 1);
    flash(t("demoBundleDone"));
  };
  const logDefect = (d) => {
    setDefects((list) => [{ id: `${d.id}-${Date.now()}`, k: d.k, bundle }, ...list].slice(0, 4));
    flash(t("demoDefectLogged"));
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]" data-testid="operator-view">
      {/* terminal */}
      <div className="surface relative overflow-hidden p-6 md:p-8">
        <div className="sweep-line opacity-50" />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="label">{t("demoStation")}</span>
            <p className="display display-md mt-2 text-[var(--warm)]">
              {STATION.line} · <span className="text-[var(--mint)]">{STATION.station}</span>
            </p>
            <p className="mt-1 text-sm text-[var(--muted)]">{pick(STATION.operator, lang)}</p>
          </div>
          <div className="text-right">
            <span className="label">{t("demoOperation")}</span>
            <p className="mt-2 text-lg font-medium text-[var(--text)]">{pick(STATION.operation, lang)}</p>
            <p className="mono mt-1 text-xs text-[var(--muted-2)]">
              {t("demoSam")} {STATION.sam} {t("demoMinutes")}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label={t("demoTargetHour")} value={STATION.targetHr} unit={t("demoPieces")} testId="op-target" />
          <Stat label={t("demoCompletedToday")} value={completed} unit={t("demoPieces")} accent testId="op-completed" />
          <Stat label={t("demoEfficiency")} value={`${efficiency}%`} testId="op-efficiency" />
          <Stat label={t("demoBundle")} value={STATION.bundle.qty} unit={t("demoPieces")} testId="op-bundle-qty" />
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div className="surface-2 flex items-center justify-between p-4">
            <div>
              <span className="label">{t("demoStyle")}</span>
              <p className="mt-1 text-sm text-[var(--text)]">{STATION.style}</p>
              <p className="text-xs text-[var(--muted-2)]">{pick(STATION.color, lang)}</p>
            </div>
            <div className="text-right">
              <span className="label">{t("demoBundle")}</span>
              <motion.p key={bundle} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mono mt-1 text-lg text-[var(--cyan)]" data-testid="op-bundle-id">
                {bundle}
              </motion.p>
            </div>
          </div>
          <button type="button" onClick={completeBundle} className="btn btn-primary justify-center !py-5 !text-base" data-testid="op-complete-bundle">
            <span>{t("demoCompleteBundle")}</span>
            <b aria-hidden="true">✓</b>
          </button>
        </div>

        <div className="mt-6">
          <span className="label">{t("demoReportDefect")}</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {STATION.defects.map((d) => (
              <button key={d.id} type="button" onClick={() => logDefect(d)} className="btn btn-outline !px-4 !py-2.5 !text-sm" data-testid={`op-defect-${d.id}`}>
                <span>{t(d.k)}</span>
              </button>
            ))}
          </div>
        </div>

        {toast && (
          <motion.p
            key={toast}
            role="status"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 border-l-2 border-[var(--mint)] pl-4 text-sm text-[var(--mint)]"
            data-testid="op-toast"
          >
            {toast}
          </motion.p>
        )}
      </div>

      {/* work standard + defects */}
      <div className="flex flex-col gap-6">
        <div className="surface p-6">
          <span className="overline">{t("demoWorkStandard")}</span>
          <ol className="mt-5 flex flex-col gap-4">
            {STATION.steps.map((s, i) => (
              <li key={i} className="grid grid-cols-[2rem_1fr] gap-3 text-sm leading-relaxed text-[var(--text)]">
                <span className="mono text-xs text-[var(--mint)]">0{i + 1}</span>
                {pick(s, lang)}
              </li>
            ))}
          </ol>
        </div>
        <div className="surface flex-1 p-6" data-testid="op-defect-log">
          <span className="overline text-[var(--cyan)]">{t("demoQuality")}</span>
          {defects.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--muted-2)]">—</p>
          ) : (
            <ul className="mt-4 divide-y divide-[var(--line)]">
              {defects.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-[var(--text)]">{t(d.k)}</span>
                  <span className="mono text-xs text-[var(--muted-2)]">{d.bundle}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
